import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store, select } from '@ngrx/store';
import { TslDialog, TslToastrService } from '@tesla/ng-tds';
import { Observable, of } from 'rxjs';
import { BankUploadFileModel } from 'app/common/bank/bank-upload-file.model';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { GeneralSettings, KeyValuePair } from 'app/profile/models/generic.model';
import { SupplierBankPropertyValue, Currency, BankConfiguration, SupplierBank, DataWithSupplierCode, SupplierDocument, SupplierDocumentPropertyValues, OPERATION, BankValidationRequest, Supplier } from 'app/profile/models/supplier.model';
import { OnboardingDocumentsService } from 'app/profile/services/onboarding-document.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { I18nService } from 'app/shared/services/i18n.service';
import { getSupplierBank, getSupplierBankPropertyValues, getSupplierDocumentPropertyValues } from 'app/shared/utils/utils';
import { BaseComponent } from '../../../base.component';
import * as GeneralActions from 'app/profile/store/actions/general.action';
import * as MasterDataActions from 'app/profile/store/actions/masterdata.action';
import * as BankDataActions from 'app/profile/store/actions/bank.actions';
import * as GeneralSelectors from 'app/profile/store/selectors/general.selector';
import * as MasterDataSelectors from 'app/profile/store/selectors/masterdata.selector';
import * as BankDataSelectors from 'app/profile/store/selectors/bank.selectors';
import * as LoaderActions from 'app/shared/store/actions/loader.actions';
import { BankCountryConfig } from 'app/profile/models/bank.model';
import { BankService } from 'app/profile/services/bank.service';
import {  map } from 'rxjs/operators';
import { GetSupplierProfileCurrentUser } from 'app/profile/store/app.actions';
import { SelectCurrentUser, SelectSupplierProfile } from 'app/profile/store/app.selector';


@Component({
  selector: 'app-bank-dialog',
  templateUrl: './bank-dialog.component.html',
  styleUrls: ['./bank-dialog.component.scss']
})
export class BankDialogComponent extends BaseComponent implements OnInit {
  initialData: SupplierBankPropertyValue;
  generalSettings$: Observable<GeneralSettings>;
  countries$: Observable<BankCountryConfig[]>;
  paymentOrigins$: Observable<KeyValuePair<string, string>[]>;
  intermediaryPaymentOrigins$: Observable<KeyValuePair<string, string>[]>;
  currencies$: Observable<Currency[]>;
  bankConfig$: Observable<BankConfiguration>;
  paymentOrigin$: Observable<KeyValuePair<string, string>[]>;
  supplierBank: SupplierBank;
  supplierBankDocType = 'BAC';
  supplierCode: string;
  selectedCountry: string;
  selectedPaymentOrigin: string;
  legalName: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: DataWithSupplierCode<SupplierBankPropertyValue>,
    public dialogRef: MatDialogRef<BankDialogComponent>,
    private dialog: TslDialog,
    private translateService: I18nService,
    private fileUploadService: FileUploadService,
    private onboardingDocumentsService: OnboardingDocumentsService,
    private supplierStateService: SupplierStateService,
    private supplierStore: Store<SupplierProfileState>,
    private toaster: TslToastrService,
    private bankService: BankService,
  ) {
    super();
    this.initialData = dialogData.data;
    this.supplierCode = dialogData.suppliercode;
    this.supplierBank = getSupplierBank(this.initialData);
  }
  ngOnInit() {
    //  Dispatch the required actions for the data that the bank-edit component needs
    this.supplierStore.dispatch(new GeneralActions.GetGeneralSettings());
    this.supplierStore.dispatch(BankDataActions.GetBankCountries({ supplierCode: this.supplierCode }));
    this.supplierStore.dispatch(new MasterDataActions.GetCurrency());
    this.supplierStore.dispatch(new GetSupplierProfileCurrentUser());

    //  Get selectors to query the required data for the bank-edit component
    this.generalSettings$ = this.supplierStore.select(GeneralSelectors.getGeneralSettings);
    this.countries$ = this.supplierStore.select(BankDataSelectors.SelectBankCountries(this.supplierCode));
    this.currencies$ = this.supplierStore.select(MasterDataSelectors.SelectCurrencies);
    this.supplierStore.pipe(select(SelectSupplierProfile)).subscribe(res => {
      this.legalName = res?.supplierDetails?.supplierName;
    })

    this.initPaymentOriginList();
  }
  /** Requests bank configuration each time the country change */
  countryChangeEventHandler(params: { countryCode: string }) {
    this.selectedCountry = params.countryCode;
    this.paymentOrigins$ = this.supplierStateService.getPaymentOrigins(this.selectedCountry)?.pipe(map(x => {
      x.forEach(pair => pair.Key = this.translateService.get(pair.Key));
      return x;
    }));
  }

  /** Requests bank configuration each time the payment origin change */
  onPaymentOriginChangedEvent(params: { paymentOrigin: string }) {
    this.selectedPaymentOrigin = params.paymentOrigin;
    this.bankConfig$ = this.supplierStateService.getBankConfiguration(this.selectedCountry, this.selectedPaymentOrigin);
  }

  /** Handler for when the bank-edit child components submits bank with client valid data */
  submitEventHandler(supplierBank: SupplierBank, uploadFiles: BankUploadFileModel[]) {
    const confirmationDialogRef = this.dialog.confirm(this.translateService.get('Confirm'), this.translateService.get('Are you sure you want to save your changes?'), this.translateService.get('OK'), this.translateService.get('Cancel'));
    confirmationDialogRef.afterClosed().subscribe((confimed:boolean) => {
      if (confimed) {
        supplierBank.ID = this.initialData?.id?.newValue;

        //  Send validation request
        this.sendValidationRequest(supplierBank)

          //  Validation request returns a collection of errors if there are any.
          //  If we don't have any error, upload the files to the server,
          //  If we do have errors, then set the error message.
          .mergeMap(errors => {


            if (!errors || errors.length <= 0) {
              let filesToUpload = uploadFiles.filter(uf => uf.fileData != null);
              let existingFiles = uploadFiles.filter(uf => uf.fileData == null)?.map(file => file.supplierDocument);

              if (filesToUpload.length > 0) {

                //  Upload files
                return this.fileUploadService.uploadFileToServer(filesToUpload.map(uf => uf.fileData), this.supplierCode)
                  .map(uploadedFile => {
                    if (uploadedFile) {
                      const document = new SupplierDocument();
                      document.Operation = OPERATION.CREATE;
                      document.DocumentID = uploadedFile.documentID;
                      document.DocumentType = 'BAC';
                      document.FileName = uploadedFile.fileName;
                      document.DocumentVersion = uploadedFile.documentVersion;
                      document.ReferenceSystem = 'DMS';
                      return [document, ...existingFiles]
                    }
                  });
              } else {

                //  Return existing files as an observable
                return of(existingFiles);
              }
            } else {
              errors.forEach(error => this.toaster.error(error, 'Bank Validation'));
              this.supplierStore.dispatch(new LoaderActions.DecrementLoaderCount());
              return of(null);
            }
          })


          //  Subscribe so that we can capture an error if any
          .subscribe((documents: SupplierDocument[]) => {
            if (documents) {

              this.dialogRef.close([getSupplierBankPropertyValues(supplierBank, this.initialData), documents.map(doc => getSupplierDocumentPropertyValues(doc, null))]);
            }
          }, err => {
            console.error(err);
          });
      }
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  /** Sends validation request to see if the bank data is valid */
  private sendValidationRequest(bank: SupplierBank): Observable<string[]> {
    //  SEND VALIDATION REQUEST ABOUT THE BANK DATA
    const validationRequest = new BankValidationRequest();
    validationRequest.bankCountryId = bank.CountryCode;
    validationRequest.paymentOrigin = bank.PaymentOrigin;

    validationRequest.isNewBankAccount = bank.IsNewBankAccount;
    validationRequest.bankAccountId = !bank.AccountNumberExists || bank.IsNewBankAccount ? bank.AccountNumber : null;
    validationRequest.bankIBAN = !bank.IBANCodeExists || bank.IsNewBankAccount ? bank.IBANCode : null;

    validationRequest.bankSwift = bank.SwiftCode;
    validationRequest.bankRoutingCode = bank.ABANumber ? bank.ABANumber.toString() : '';

    validationRequest.beneficiaryNameEnglish = bank.BeneficiaryNameEnglish;
    validationRequest.beneficiaryNameLocalLanguage = bank.BeneficiaryNameLocal;

    validationRequest.paymentType = bank.PaymentType;
    validationRequest.bankAccountKatanaka = bank.BankAccountNameKatanaka;

    //  Address fields
    validationRequest.streetAddressLine1 = bank.Address1;
    validationRequest.streetAddressLine2 = bank.Address2;
    validationRequest.city = bank.City;
    validationRequest.state = bank.State;
    validationRequest.zipCode = bank.ZipCode?.toString();
    validationRequest.id = bank.ID;
    validationRequest.verificationStatus = bank.VerificationStatus;
    validationRequest.verificationTransactionId = bank.VerificationTransactionId;

    this.supplierStore.dispatch(new LoaderActions.IncrementLoaderCount());
    return this.supplierStateService.validateBankConfiguration(this.supplierCode, validationRequest);
  }

  saveEventHandler(bank: SupplierBank) {
    this.bankService.saveDraftBank(this.supplierCode, bank)
      .subscribe(response => {
        this.dialogRef.close();
      });
  }

  initPaymentOriginList() {
    let Domestic = new KeyValuePair<string, string>();
    Domestic.Value = "Domestic";
    Domestic.Key = this.translateService.get("Domestic");
    let CrossBorder = new KeyValuePair<string, string>();
    CrossBorder.Value = "CrossBorder";
    CrossBorder.Key = this.translateService.get("CrossBorder");
    let Both = new KeyValuePair<string, string>();
    Both.Value = "Both";
    Both.Key = this.translateService.get("Both");
    this.intermediaryPaymentOrigins$ = of([Domestic, CrossBorder, Both]);
  }
}

